// components/business/BusinessManager.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "../common/Layout";
import BusinessDetails from "./BusinessDetails";
import BusinessTerminals from "./BusinessTerminals";
import BusinessTerminalSettings from "./BusinessTerminalSettings";
import Toast from "../common/Toast";
import Loader from "../common/Loader";
import DataTable from "react-data-table-component";
import Select from "react-select";
import {
  getBusinesses,
  addBusiness,
  getBusinessById,
  getBusinessTypes,
} from "../../api/businessApi";

const VALID_TABS = ["details", "terminals", "settings"];

const BusinessManager = () => {
  // ============ URL SYNC ============
  const [searchParams, setSearchParams] = useSearchParams();

  const urlBusinessId = searchParams.get("b");
  const urlTab = searchParams.get("tab");

  const initialTab = urlTab && VALID_TABS.includes(urlTab) ? urlTab : "details";

  // ============ STATE MANAGEMENT ============
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [isLoadingBusinessDetails, setIsLoadingBusinessDetails] =
    useState(false);

  const [activeTab, setActiveTab] = useState(initialTab);
  const [hasTerminals, setHasTerminals] = useState(false);

  const [toast, setToast] = useState({ message: "", type: "" });
  const [filterText, setFilterText] = useState("");
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);

  // On first render, if URL has a business id, we start in details view
  const [showBusinessList, setShowBusinessList] = useState(!urlBusinessId);
  const [activeMenu, setActiveMenu] = useState("business");

  const [showAddBusinessModal, setShowAddBusinessModal] = useState(false);
  const [addBusinessLoading, setAddBusinessLoading] = useState(false);

  const [businessTypes, setBusinessTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const typesLoadedRef = useRef(false);

  // Refs to prevent double calls
  const loadingRef = useRef(false);
  const restoreAttemptedRef = useRef(false);
  // Tracks which businessId has already been fetched so we never fetch twice
  const fetchedBusinessIdRef = useRef(null);

  // Add business form data
  const [addBusinessFormData, setAddBusinessFormData] = useState({
    companyName: "",
    tradingName: "",
    directorName: "",
    businessAddress: "",
    businessType: "",
    mobile: "",
    email: "",
    acquirerMId: "",
    pFeeCap: "",
    dFeeCap: "",
    latLng: { lat: 0, lng: 0 },
  });

  // ============ GOOGLE PLACES ============
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const addressInputRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 40;
    let timeoutId = null;

    const tryInit = () => {
      if (cancelled) return;

      const googleReady =
        typeof window !== "undefined" &&
        window.google &&
        window.google.maps &&
        window.google.maps.places &&
        window.google.maps.places.AutocompleteService;

      if (googleReady) {
        try {
          autocompleteServiceRef.current =
            new window.google.maps.places.AutocompleteService();
          placesServiceRef.current =
            new window.google.maps.places.PlacesService(
              document.createElement("div"),
            );
        } catch (err) {}
        return;
      }

      attempts += 1;
      if (attempts >= MAX_ATTEMPTS) {
        console.warn(
          "Google Places API did not load in time. Address autocomplete disabled.",
        );
        return;
      }

      timeoutId = setTimeout(tryInit, 500);
    };

    tryInit();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  // ============ HELPERS ============
  const showToast = useCallback(
    (message, type = "success", manualClose = false) => {
      setToast({ message, type, manualClose });
    },
    [],
  );

  const updateUrl = useCallback(
    (businessId, tab) => {
      const params = new URLSearchParams();
      if (businessId) params.set("b", businessId);
      if (tab && tab !== "details") params.set("tab", tab);
      setSearchParams(params, { replace: true });
    },
    [setSearchParams],
  );

  const loadBusinessTypes = useCallback(async () => {
    if (typesLoadedRef.current) return;
    typesLoadedRef.current = true;
    try {
      setLoadingTypes(true);
      const types = await getBusinessTypes();
      setBusinessTypes(types);
    } catch (error) {
      typesLoadedRef.current = false;
    } finally {
      setLoadingTypes(false);
    }
  }, []);

  const loadBusinesses = useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);

    try {
      const data = await getBusinesses();
      setBusinesses(Array.isArray(data) ? data : []);
    } catch (error) {
      showToast("Failed to load businesses", "danger");
      setBusinesses([]);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [showToast]);

  // Helper: apply a business payload to state + hasTerminals
  const applyBusinessDetails = useCallback((details) => {
    if (!details) return;
    const hasTerminalsData =
      details.terminals?.length > 0 || details.hasTerminals || false;
    setHasTerminals(hasTerminalsData);
    setSelectedBusiness(details);
  }, []);

  // ============ RESTORE FROM URL (only once, only on first mount) ============
  useEffect(() => {
    if (restoreAttemptedRef.current) return;
    if (!urlBusinessId) return;

    restoreAttemptedRef.current = true;

    const restore = async () => {
      setShowBusinessList(false);

      // Terminals AND Settings tabs only need the business id.
      // Terminals loads its own list; Settings loads its own data via getSetting.
      if (initialTab === "terminals" || initialTab === "settings") {
        setSelectedBusiness({
          id: urlBusinessId,
          businessGuid: urlBusinessId,
        });
        setActiveTab(initialTab);
        setIsLoadingBusinessDetails(false);
        return;
      }

      // Avoid double fetch if we already fetched this business
      if (fetchedBusinessIdRef.current === urlBusinessId) return;

      setIsLoadingBusinessDetails(true);
      try {
        fetchedBusinessIdRef.current = urlBusinessId;
        const businessDetails = await getBusinessById(urlBusinessId);

        if (businessDetails) {
          applyBusinessDetails(businessDetails);

          const hasTerminalsData =
            businessDetails.terminals?.length > 0 ||
            businessDetails.hasTerminals ||
            false;

          const safeTab =
            initialTab === "settings" && !hasTerminalsData
              ? "details"
              : initialTab;
          setActiveTab(safeTab);

          if (safeTab !== initialTab) {
            updateUrl(urlBusinessId, safeTab);
          }
        } else {
          updateUrl(null, null);
          setShowBusinessList(true);
        }
      } catch (error) {
        updateUrl(null, null);
        setShowBusinessList(true);
      } finally {
        setIsLoadingBusinessDetails(false);
      }
    };

    restore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============ LIST LOAD: only when list view is shown ============
  useEffect(() => {
    if (!showBusinessList) return;
    loadBusinesses();
  }, [showBusinessList, loadBusinesses]);

  // ============ SELECT BUSINESS ============
  const handleBusinessSelect = useCallback(
    async (business) => {
      const businessId = business.businessGuid || business.id;
      if (!businessId) {
        showToast("Invalid business ID", "danger");
        return;
      }

      setShowBusinessList(false);
      setSelectedBusiness(business);
      setActiveTab("details");
      updateUrl(businessId, "details");

      // Skip fetch if we already fetched this business
      if (
        fetchedBusinessIdRef.current === businessId &&
        selectedBusiness?.id === businessId
      ) {
        return;
      }

      setIsLoadingBusinessDetails(true);
      try {
        fetchedBusinessIdRef.current = businessId;
        const businessDetails = await getBusinessById(businessId);
        if (businessDetails) {
          applyBusinessDetails(businessDetails);
          setActiveTab("details");
        } else {
          showToast("Failed to load business details", "danger");
        }
      } catch (error) {
        showToast("Failed to load business details", "danger");
      } finally {
        setIsLoadingBusinessDetails(false);
      }
    },
    [applyBusinessDetails, selectedBusiness, showToast, updateUrl],
  );

  // ============ BACK TO LIST ============
  const handleBackToList = useCallback(() => {
    setShowBusinessList(true);
    setSelectedBusiness(null);
    setHasTerminals(false);
    setIsLoadingBusinessDetails(false);
    setActiveTab("details");
    fetchedBusinessIdRef.current = null;
    updateUrl(null, null);
  }, [updateUrl]);

  // ============ TAB CHANGE ============
  const handleTabChange = useCallback(
    async (tab) => {
      setActiveTab(tab);
      const businessId =
        selectedBusiness?.businessGuid || selectedBusiness?.id || urlBusinessId;
      if (businessId) updateUrl(businessId, tab);

      // Refetch business details when switching back to "details" tab
      // so any changes made in other tabs (or by other users) are reflected.
      if (tab === "details" && businessId) {
        setIsLoadingBusinessDetails(true);
        try {
          fetchedBusinessIdRef.current = businessId;
          const businessDetails = await getBusinessById(businessId);
          if (businessDetails) {
            applyBusinessDetails(businessDetails);
          }
        } catch (error) {
          showToast("Failed to load business details", "danger");
        } finally {
          setIsLoadingBusinessDetails(false);
        }
      }
    },
    [
      selectedBusiness,
      urlBusinessId,
      updateUrl,
      applyBusinessDetails,
      showToast,
    ],
  );

  // ============ SAVE BUSINESS DETAILS ============
  const handleSave = useCallback(
    async (updatedBusinessData, needsReload = false) => {
      try {
        if (needsReload) {
          // Settings tab handles its own refetch via getSetting
          if (activeTab === "settings") return;

          const bid =
            selectedBusiness?.businessGuid ||
            selectedBusiness?.id ||
            urlBusinessId;
          if (bid) {
            const fresh = await getBusinessById(bid);
            if (fresh) applyBusinessDetails(fresh);
          }
          return;
        }

        if (updatedBusinessData) {
          applyBusinessDetails(updatedBusinessData);

          const bid =
            updatedBusinessData.businessGuid ||
            updatedBusinessData.id ||
            urlBusinessId;
          if (bid) updateUrl(bid, activeTab);

          showToast("Business updated successfully");
        }
      } catch (error) {
        showToast("Failed to update business", "danger");
      }
    },
    [
      applyBusinessDetails,
      showToast,
      updateUrl,
      activeTab,
      selectedBusiness,
      urlBusinessId,
    ],
  );

  // ============ TERMINALS UPDATED (fires on terminal list load AND on save) ============
  // Second arg is the actual terminal count from BusinessTerminals, so the
  // Properties tab shows up the moment terminals are loaded — not only after a save.
  const handleTerminalsUpdated = useCallback(
    (updatedBusinessData, terminalCount) => {
      if (updatedBusinessData) {
        setSelectedBusiness(updatedBusinessData);
      }

      // Determine hasTerminals from the count first, then from the payload
      let hasTerminalsData = false;
      if (typeof terminalCount === "number") {
        hasTerminalsData = terminalCount > 0;
      } else if (updatedBusinessData) {
        hasTerminalsData =
          updatedBusinessData.terminals?.length > 0 ||
          updatedBusinessData.hasTerminals ||
          false;
      }

      setHasTerminals(hasTerminalsData);

      // If we're on settings and terminals went away, fall back to details
      if (activeTab === "settings" && !hasTerminalsData) {
        setActiveTab("details");
        const bid =
          updatedBusinessData?.businessGuid ||
          updatedBusinessData?.id ||
          urlBusinessId;
        if (bid) updateUrl(bid, "details");
      }
    },
    [activeTab, updateUrl, urlBusinessId],
  );

  // ============ MENU CHANGE ============
  const handleMenuChange = useCallback(
    (menu) => {
      setActiveMenu(menu);

      if (menu === "business") {
        setShowBusinessList(true);
        setSelectedBusiness(null);
        setHasTerminals(false);
        setActiveTab("details");
        fetchedBusinessIdRef.current = null;
        updateUrl(null, null);
        // list effect will fire because showBusinessList changed to true
      }
    },
    [updateUrl],
  );

  // ============ ADD BUSINESS ADDRESS AUTOCOMPLETE ============
  const handleAddAddressInput = async (value) => {
    setAddBusinessFormData({ ...addBusinessFormData, businessAddress: value });

    if (value.length > 2 && autocompleteServiceRef.current) {
      try {
        const request = {
          input: value,
          types: ["address"],
          componentRestrictions: { country: ["au", "nz"] },
        };

        autocompleteServiceRef.current.getPlacePredictions(
          request,
          (predictions, status) => {
            if (
              status === window.google.maps.places.PlacesServiceStatus.OK &&
              predictions
            ) {
              setAddressSuggestions(predictions);
              setShowSuggestions(true);
            } else {
              setAddressSuggestions([]);
              setShowSuggestions(false);
            }
          },
        );
      } catch (error) {
        setAddressSuggestions([]);
        setShowSuggestions(false);
      }
    } else {
      setAddressSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleAddAddressSelect = (suggestion) => {
    setShowSuggestions(false);
    setAddBusinessFormData({
      ...addBusinessFormData,
      businessAddress: suggestion.description,
    });

    if (placesServiceRef.current) {
      const request = {
        placeId: suggestion.place_id,
        fields: ["geometry", "formatted_address"],
      };

      placesServiceRef.current.getDetails(request, (place, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          place &&
          place.geometry
        ) {
          setAddBusinessFormData((prev) => ({
            ...prev,
            businessAddress: place.formatted_address || prev.businessAddress,
            latLng: {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
            },
          }));
        }
      });
    }
  };

  const handleAddAddressBlur = () => {
    setTimeout(() => setShowSuggestions(false), 200);
  };

  const handleAddAddressFocus = () => {
    if (
      addBusinessFormData.businessAddress &&
      addBusinessFormData.businessAddress.length > 2
    ) {
      handleAddAddressInput(addBusinessFormData.businessAddress);
    }
  };

  // ============ ADD BUSINESS MODAL ============
  const handleAddBusinessClick = useCallback(() => {
    setAddBusinessFormData({
      companyName: "",
      tradingName: "",
      directorName: "",
      businessAddress: "",
      businessType: "",
      mobile: "",
      email: "",
      acquirerMId: "",
      pFeeCap: "",
      dFeeCap: "",
      latLng: { lat: 0, lng: 0 },
    });
    setAddressSuggestions([]);
    setShowSuggestions(false);
    setShowAddBusinessModal(true);
    loadBusinessTypes();
  }, [loadBusinessTypes]);

  const handleAddBusinessSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setAddBusinessLoading(true);

      try {
        const newBusiness = {
          companyName: addBusinessFormData.companyName,
          tradingName:
            addBusinessFormData.tradingName || addBusinessFormData.companyName,
          directorName: addBusinessFormData.directorName,
          businessAddress: addBusinessFormData.businessAddress,
          businessType: addBusinessFormData.businessType || "Other",
          mobile: addBusinessFormData.mobile,
          email: addBusinessFormData.email,
          acquirerMId: addBusinessFormData.acquirerMId,
          pFeeCap: addBusinessFormData.pFeeCap || "0",
          dFeeCap: addBusinessFormData.dFeeCap || "0",
          latLng: addBusinessFormData.latLng || { lat: 0, lng: 0 },
        };

        await addBusiness(newBusiness);
        showToast("Business added successfully");
        setShowAddBusinessModal(false);
        await loadBusinesses();
      } catch (error) {
        if (error.statusCode === 409) {
          const conflictMessage =
            error.details || "Conflict detected. Please refresh and try again.";
          showToast(`${conflictMessage}`, "warning", true);
          await loadBusinesses();
          setShowAddBusinessModal(false);
        } else {
          const errorMsg =
            error.response?.data?.message || "Failed to add business";
          showToast(errorMsg, "danger");
        }
      }
      setAddBusinessLoading(false);
    },
    [addBusinessFormData, loadBusinesses, showToast],
  );

  // ============ FILTER BUSINESSES ============
  const filteredBusinesses = useCallback(() => {
    if (!Array.isArray(businesses)) return [];

    const searchTerm = filterText.toLowerCase().trim();
    if (!searchTerm) return businesses;

    return businesses.filter((item) => {
      const searchableFields = [
        item.companyName,
        item.tradingName,
        item.directorName,
        item.businessType,
        item.businessAddress,
        item.mobile,
        item.email,
        item.acquirerMId,
      ];

      return searchableFields.some(
        (field) => field && String(field).toLowerCase().includes(searchTerm),
      );
    });
  }, [businesses, filterText]);

  // ============ TABLE COLUMNS ============
  const columns = [
    {
      name: "Merchant Id",
      selector: (row) => row.acquirerMId || "N/A",
      sortable: true,
      cell: (row) => (
        <span
          className="business-name-link"
          onClick={() => handleBusinessSelect(row)}
          style={{ cursor: "pointer", color: "var(--color-blaze-orange)" }}
        >
          {row.acquirerMId || "N/A"}
        </span>
      ),
    },
    {
      name: "Trading Name",
      selector: (row) => row.tradingName || "N/A",
      sortable: true,
    },
    {
      name: "Company Name",
      selector: (row) => row.companyName || "N/A",
      sortable: true,
      grow: 2,
    },
    {
      name: "Director Name",
      selector: (row) => row.directorName || "N/A",
      sortable: true,
    },
    {
      name: "Business Type",
      selector: (row) => row.businessType || "N/A",
      sortable: true,
    },
    {
      name: "Mobile",
      selector: (row) => row.mobile || "N/A",
      sortable: true,
    },
  ];

  const handleSearchChange = useCallback(
    (e) => {
      setFilterText(e.target.value);
      setResetPaginationToggle(!resetPaginationToggle);
    },
    [resetPaginationToggle],
  );

  const clearSearch = useCallback(() => {
    setFilterText("");
    setResetPaginationToggle(!resetPaginationToggle);
  }, [resetPaginationToggle]);

  // ============ RENDER ============
  return (
    <Layout
      activeMenu={activeMenu}
      onMenuChange={handleMenuChange}
      title="Business Management"
    >
      {showBusinessList ? (
        <div className="card shadow-sm">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <div className="position-relative">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search..."
                  value={filterText}
                  onChange={handleSearchChange}
                  style={{ width: "300px", paddingRight: "35px" }}
                />
                {filterText && (
                  <button
                    className="btn btn-link position-absolute search-clear-btn"
                    onClick={clearSearch}
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      padding: 0,
                      border: "none",
                      background: "transparent",
                    }}
                  >
                    <i className="bi bi-x-circle"></i>
                  </button>
                )}
              </div>
              <button
                className="btn btn-primary"
                onClick={handleAddBusinessClick}
              >
                <i className="bi bi-plus-circle me-1"></i>Add Business
              </button>
            </div>
            <DataTable
              columns={columns}
              data={filteredBusinesses()}
              progressPending={loading}
              progressComponent={
                <Loader message="Loading businesses..." size="medium" />
              }
              pagination
              paginationRowsPerPageOptions={[5, 10, 25, 50]}
              paginationPerPage={10}
              paginationResetDefaultPage={resetPaginationToggle}
              persistTableHead
              responsive
              highlightOnHover
              striped
              className="business-table"
              noDataComponent={
                <div className="text-center py-5">
                  <i
                    className="bi bi-inbox display-1"
                    style={{ color: "var(--color-peach-glow)" }}
                  ></i>
                  <p className="text-muted mt-3">
                    {filterText
                      ? "No businesses match your search"
                      : "No businesses found. Click 'Add Business' to get started!"}
                  </p>
                  {!filterText && (
                    <button
                      className="btn btn-primary mt-2"
                      onClick={handleAddBusinessClick}
                    >
                      <i className="bi bi-plus-circle me-1"></i>Add Business
                    </button>
                  )}
                </div>
              }
            />
          </div>
        </div>
      ) : (
        <div className="card shadow-sm">
          <div className="card-header d-flex justify-content-between align-items-center">
            <div>
              <button
                className="btn btn-outline-primary btn-sm me-2"
                onClick={handleBackToList}
              >
                <i className="bi bi-arrow-left me-1"></i>Back
              </button>
              <span className="fw-bold">
                {selectedBusiness?.companyName ||
                  selectedBusiness?.tradingName ||
                  "Business Details"}
              </span>
            </div>
          </div>

          <div className="card-body p-0">
            <ul
              className="nav nav-tabs px-3 pt-3"
              style={{ borderBottom: "2px solid var(--color-peach-glow)" }}
            >
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "details" ? "active" : ""}`}
                  onClick={() => handleTabChange("details")}
                  style={{
                    color:
                      activeTab === "details"
                        ? "var(--color-blaze-orange)"
                        : "var(--color-black)",
                    fontWeight: activeTab === "details" ? "600" : "400",
                    borderBottom:
                      activeTab === "details"
                        ? "3px solid var(--color-blaze-orange)"
                        : "none",
                  }}
                >
                  <i className="bi bi-info-circle me-1"></i>Details
                </button>
              </li>

              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "terminals" ? "active" : ""}`}
                  onClick={() => handleTabChange("terminals")}
                  style={{
                    color:
                      activeTab === "terminals"
                        ? "var(--color-blaze-orange)"
                        : "var(--color-black)",
                    fontWeight: activeTab === "terminals" ? "600" : "400",
                    borderBottom:
                      activeTab === "terminals"
                        ? "3px solid var(--color-blaze-orange)"
                        : "none",
                  }}
                >
                  <i className="bi bi-terminal me-1"></i>Terminals
                </button>
              </li>

              {(hasTerminals || activeTab === "settings") && (
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === "settings" ? "active" : ""}`}
                    onClick={() => handleTabChange("settings")}
                    style={{
                      color:
                        activeTab === "settings"
                          ? "var(--color-blaze-orange)"
                          : "var(--color-black)",
                      fontWeight: activeTab === "settings" ? "600" : "400",
                      borderBottom:
                        activeTab === "settings"
                          ? "3px solid var(--color-blaze-orange)"
                          : "none",
                    }}
                  >
                    <i className="bi bi-sliders me-1"></i>Properties
                  </button>
                </li>
              )}
            </ul>

            <div className="p-3">
              {isLoadingBusinessDetails ? (
                <Loader message="Fetching business details..." size="large" />
              ) : selectedBusiness ? (
                <>
                  {activeTab === "details" && (
                    <BusinessDetails
                      business={selectedBusiness}
                      onSave={handleSave}
                      showToast={showToast}
                      businessTypes={businessTypes}
                      loadingTypes={loadingTypes}
                    />
                  )}

                  {activeTab === "terminals" && (
                    <BusinessTerminals
                      businessId={
                        selectedBusiness.id || selectedBusiness.businessGuid
                      }
                      showToast={showToast}
                      onTerminalsUpdated={handleTerminalsUpdated}
                    />
                  )}

                  {activeTab === "settings" && (
                    <BusinessTerminalSettings
                      businessId={
                        selectedBusiness.id || selectedBusiness.businessGuid
                      }
                      showToast={showToast}
                      onSettingsUpdated={handleSave}
                    />
                  )}
                </>
              ) : (
                <div className="text-center py-5">
                  <i className="bi bi-exclamation-circle display-4 text-warning"></i>
                  <p className="text-muted mt-2">No business data available</p>
                  <button
                    className="btn btn-primary"
                    onClick={handleBackToList}
                  >
                    <i className="bi bi-arrow-left me-1"></i>Go Back
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Business modal — unchanged */}
      {showAddBusinessModal && (
        <div className="modal d-block" style={{ zIndex: 1051 }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-building me-2"></i>
                  Add New Business
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowAddBusinessModal(false);
                    setShowSuggestions(false);
                  }}
                  disabled={addBusinessLoading}
                ></button>
              </div>

              <form onSubmit={handleAddBusinessSubmit}>
                <div className="modal-body">
                  {addBusinessLoading ? (
                    <Loader message="Adding business..." size="medium" />
                  ) : (
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Merchant ID *</label>
                        <input
                          type="text"
                          className="form-control"
                          value={addBusinessFormData.acquirerMId}
                          onChange={(e) => {
                            const cleaned = e.target.value
                              .replace(/[^a-zA-Z0-9]/g, "")
                              .toUpperCase();
                            setAddBusinessFormData({
                              ...addBusinessFormData,
                              acquirerMId: cleaned,
                            });
                          }}
                          onKeyDown={(e) => {
                            const allowed =
                              /[a-zA-Z0-9]/.test(e.key) ||
                              [
                                "Backspace",
                                "Delete",
                                "ArrowLeft",
                                "ArrowRight",
                                "Tab",
                                "Home",
                                "End",
                              ].includes(e.key) ||
                              e.ctrlKey ||
                              e.metaKey;
                            if (!allowed) e.preventDefault();
                          }}
                          placeholder="Enter merchant ID"
                          minLength={5}
                          maxLength={25}
                          required
                          disabled={addBusinessLoading}
                          pattern="[A-Z0-9]+"
                          title="Only uppercase letters and numbers are allowed (5–25 characters)"
                        />
                      </div>
                      <div className="col-md-6 mb-3"></div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Company Name *</label>
                        <input
                          type="text"
                          className="form-control"
                          value={addBusinessFormData.companyName}
                          onChange={(e) =>
                            setAddBusinessFormData({
                              ...addBusinessFormData,
                              companyName: e.target.value,
                            })
                          }
                          placeholder="Enter company name"
                          required
                          disabled={addBusinessLoading}
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Trading Name</label>
                        <input
                          type="text"
                          className="form-control"
                          value={addBusinessFormData.tradingName}
                          onChange={(e) =>
                            setAddBusinessFormData({
                              ...addBusinessFormData,
                              tradingName: e.target.value,
                            })
                          }
                          placeholder="Enter trading name"
                          disabled={addBusinessLoading}
                        />
                        <small className="text-muted">
                          Optional - uses Company Name if blank
                        </small>
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Director Name *</label>
                        <input
                          type="text"
                          className="form-control"
                          value={addBusinessFormData.directorName}
                          onChange={(e) =>
                            setAddBusinessFormData({
                              ...addBusinessFormData,
                              directorName: e.target.value,
                            })
                          }
                          placeholder="Enter director name"
                          required
                          disabled={addBusinessLoading}
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Business Address *</label>
                        <div className="position-relative">
                          <input
                            ref={addressInputRef}
                            type="text"
                            className="form-control"
                            value={addBusinessFormData.businessAddress}
                            onChange={(e) =>
                              handleAddAddressInput(e.target.value)
                            }
                            onFocus={handleAddAddressFocus}
                            onBlur={handleAddAddressBlur}
                            placeholder="Start typing your business address..."
                            required
                            disabled={addBusinessLoading}
                          />
                          {showSuggestions && addressSuggestions.length > 0 && (
                            <ul
                              className="list-group position-absolute w-100 mt-1 shadow-lg"
                              style={{ zIndex: 1060 }}
                            >
                              {addressSuggestions.map((suggestion) => (
                                <li
                                  key={suggestion.place_id}
                                  className="list-group-item list-group-item-action"
                                  style={{ cursor: "pointer" }}
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    handleAddAddressSelect(suggestion);
                                  }}
                                >
                                  <strong>
                                    {suggestion.structured_formatting
                                      ?.main_text || suggestion.description}
                                  </strong>
                                  <br />
                                  <small className="text-muted">
                                    {suggestion.structured_formatting
                                      ?.secondary_text || ""}
                                  </small>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                        {/* {addBusinessFormData.latLng &&
                          addBusinessFormData.latLng.lat !== 0 && (
                            <small className="text-muted mt-1 d-block">
                              <i className="bi bi-geo-alt me-1"></i>
                              Latitude:{" "}
                              {addBusinessFormData.latLng.lat.toFixed(6)},
                              Longitude:{" "}
                              {addBusinessFormData.latLng.lng.toFixed(6)}
                            </small>
                          )} */}
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Mobile Number *</label>
                        <input
                          type="text"
                          className="form-control"
                          value={addBusinessFormData.mobile}
                          onChange={(e) =>
                            setAddBusinessFormData({
                              ...addBusinessFormData,
                              mobile: e.target.value,
                            })
                          }
                          placeholder="Enter mobile number"
                          required
                          disabled={addBusinessLoading}
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Email *</label>
                        <input
                          type="email"
                          className="form-control"
                          value={addBusinessFormData.email}
                          onChange={(e) =>
                            setAddBusinessFormData({
                              ...addBusinessFormData,
                              email: e.target.value,
                            })
                          }
                          placeholder="Enter email address"
                          required
                          disabled={addBusinessLoading}
                        />
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label">Business Type *</label>
                        <Select
                          options={businessTypes.map((type) => ({
                            value: type.id || type.value,
                            label: type.typeDesc || type.label || type.name,
                          }))}
                          value={
                            addBusinessFormData.businessType
                              ? {
                                  value: addBusinessFormData.businessType,
                                  label:
                                    businessTypes.find(
                                      (t) =>
                                        (t.id || t.value) ===
                                        addBusinessFormData.businessType,
                                    )?.typeDesc ||
                                    businessTypes.find(
                                      (t) =>
                                        (t.id || t.value) ===
                                        addBusinessFormData.businessType,
                                    )?.label ||
                                    addBusinessFormData.businessType,
                                }
                              : null
                          }
                          onChange={(selected) => {
                            setAddBusinessFormData({
                              ...addBusinessFormData,
                              businessType: selected ? selected.value : "",
                            });
                          }}
                          isLoading={loadingTypes}
                          placeholder="Select Business Type"
                          isClearable
                          isSearchable
                          className="react-select-container"
                          classNamePrefix="react-select"
                          required
                        />
                        {loadingTypes && (
                          <small className="text-muted">Loading types...</small>
                        )}
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label">FeeCap(%)</label>
                        <input
                          type="number"
                          step="0.01"
                          className="form-control"
                          value={addBusinessFormData.pFeeCap}
                          onChange={(e) =>
                            setAddBusinessFormData({
                              ...addBusinessFormData,
                              pFeeCap: e.target.value,
                            })
                          }
                          placeholder="Enter %"
                          disabled={addBusinessLoading}
                        />
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label">FeeCap($)</label>
                        <input
                          type="number"
                          step="0.01"
                          className="form-control"
                          value={addBusinessFormData.dFeeCap}
                          onChange={(e) =>
                            setAddBusinessFormData({
                              ...addBusinessFormData,
                              dFeeCap: e.target.value,
                            })
                          }
                          placeholder="Enter $"
                          disabled={addBusinessLoading}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowAddBusinessModal(false);
                      setShowSuggestions(false);
                    }}
                    disabled={addBusinessLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={addBusinessLoading}
                  >
                    {addBusinessLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Adding...
                      </>
                    ) : (
                      "Add Business"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <Toast
        message={toast.message}
        type={toast.type}
        manualClose={toast.manualClose}
        onClose={() => setToast({ message: "", type: "", manualClose: false })}
      />
    </Layout>
  );
};

export default BusinessManager;
